import { GlobalMenu, DeleteDialog, CreateUpdatesDialog } from '@components';
import { useToast } from '@hooks';
import { NavigationContext } from '@layout/navigation/navigation';
import { IUpdateRawQueryList } from '@models/Update.model';
import { UpdateStatusType } from '@models/UpdateStatus.model';
import {
  changeUpdateStatusByUpdate,
  deleteUpdate,
} from '@services/queries/updates';
import { useState as useHookState } from '@hookstate/core';
import { changeUpdateStatus } from '@services/queries/updates/changeUpdateStatus/changeUpdateStatus';

import { useContext, useState } from 'react';
import { useQueryClient, useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import { InviteUserRole } from '@models/Invite.model';

import { useAppDispatch } from '@store/redux/store';
import {
  addBackUpUpdatedUpdates,
  addDeletedUpdate,
  addUpdatedUpdate,
} from '@store/redux/features/updatesOptimistic/updatesOptimisticSlice';
import { AffectedItemsDialog } from '../Components/AffectedItemsDialog/AffectedItemsDialog';
import { PriorityDialog } from '../Components/PriorityDialog/PriorityDialog';
import { Container } from './NewTableMenu.styles';

interface INewTableMenu {
  updateStatus: IUpdateRawQueryList;
}

const NewTableMenu = ({ updateStatus }: INewTableMenu) => {
  const params = useParams<{ id: string; equipmentId: string }>();
  const { studioId, role, updatesMtViewTime } = useContext(NavigationContext);

  const { toast } = useToast();

  const dispatch = useAppDispatch();

  const queryClient = useQueryClient();

  const openDeleteDialog = useHookState(false);

  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);

  const [openPriorityDialog, setOpenPriorityDialog] = useState<boolean>(false);
  const [openAffectedItemsDialog, setOpenAffectedItemsDialog] =
    useState<boolean>(false);

  const mutationByUpdate = useMutation(changeUpdateStatusByUpdate, {
    onSuccess: async newUpdate => {
      dispatch(addBackUpUpdatedUpdates(newUpdate));
      dispatch(addUpdatedUpdate({ newUpdate, updatesMtViewTime }));

      toast.success('Update changed successfully');
      queryClient.invalidateQueries(['updateStatuses']);
      queryClient.invalidateQueries(['updatesMetrics']);
      queryClient.invalidateQueries(['updateStatus', updateStatus.id]);
      queryClient.invalidateQueries(['studios', params.id, 'updateStatuses']);
      queryClient.invalidateQueries([
        'equipments',
        params.equipmentId,
        'updateStatuses',
      ]);
    },
    onError: () => {
      toast.success('Something got wrong');
    },
  });

  const mutation = useMutation(changeUpdateStatus, {
    onSuccess: async newUpdate => {
      dispatch(addBackUpUpdatedUpdates(newUpdate));
      dispatch(addUpdatedUpdate({ newUpdate, updatesMtViewTime }));

      toast.success('Update changed successfully');
      queryClient.invalidateQueries(['updateStatuses']);
      queryClient.invalidateQueries(['updatesMetrics']);
      queryClient.invalidateQueries(['updateStatus', updateStatus.id]);
      queryClient.invalidateQueries(['studios', params.id, 'updateStatuses']);
      queryClient.invalidateQueries([
        'equipments',
        params.equipmentId,
        'updateStatuses',
      ]);
    },
    onError: () => {
      toast.success('Something got wrong');
    },
  });

  const mutationDelete = useMutation(deleteUpdate, {
    onSuccess: async () => {
      toast.success('Update was deleted successfully');
      openDeleteDialog.set(false);

      dispatch(
        addDeletedUpdate({ updateId: updateStatus.id, updatesMtViewTime }),
      );

      queryClient.invalidateQueries(['updateStatuses']);
      queryClient.invalidateQueries(['updatesMetrics']);
      queryClient.invalidateQueries(['updateStatus', updateStatus.id]);
      queryClient.invalidateQueries(['studios', params.id, 'updateStatuses']);
      queryClient.invalidateQueries([
        'equipments',
        params.equipmentId,
        'updateStatuses',
      ]);
    },
    onError: () => {
      toast.error('Something got wrong');
    },
  });

  const options = () => {
    const initialOptions = [
      {
        disabled: ![InviteUserRole.ADMIN].includes(role),
        disableToolTip: [InviteUserRole.ADMIN].includes(role),
        toolTipText: 'Only admins can prioritize updates.',
        toolTipId: 'prioritizeTooltip',
        label: 'Prioritize',
        iconName: 'flag',
        onClick: () => {
          setOpenPriorityDialog(true);
        },
        bottomSeparator: true,
      },
      {
        label: 'See Affected Assets',
        iconName: 'cube',
        bottomSeparator: false,
        onClick: () => {
          setOpenAffectedItemsDialog(true);
        },
      },
      {
        disabled: ![InviteUserRole.ADMIN].includes(role),
        disableToolTip: [InviteUserRole.ADMIN].includes(role),
        toolTipText: 'Only admins can dismiss updates.',
        toolTipId: 'dismissTooltip',
        label: 'Dismiss',
        iconName: 'delete',
        onClick: () => {
          if (!mutation.isLoading && !mutationByUpdate.isLoading) {
            if (updateStatus.usId) {
              mutation.mutate({
                updateStatusId: updateStatus.usId,
                type: UpdateStatusType.DISMISSED,
              });
            } else {
              mutationByUpdate.mutate({
                updateId: updateStatus.id,
                studioId,
                type: UpdateStatusType.DISMISSED,
              });
            }
          }
        },
      },
    ];

    if (updateStatus.usStudioId) {
      initialOptions.unshift({
        label: 'Delete Update',
        iconName: 'trash',
        bottomSeparator: true,
        onClick: () => {
          openDeleteDialog.set(true);
        },
      });
      initialOptions.unshift({
        label: 'Edit Update',
        iconName: 'edit',
        bottomSeparator: false,
        onClick: () => {
          setOpenEditDialog(true);
        },
      });
    }

    return initialOptions;
  };

  return (
    <Container>
      <GlobalMenu
        align="end"
        hoverColor="#E6E8F0"
        title="Options"
        itemList={options()}
      />

      {openEditDialog && (
        <CreateUpdatesDialog
          open={openEditDialog}
          edit={{ updatePrev: updateStatus }}
          onClose={() => setOpenEditDialog(false)}
        />
      )}

      <DeleteDialog
        open={openDeleteDialog}
        onDelete={() => {
          mutationDelete.mutate(updateStatus.id);
        }}
      />

      <PriorityDialog
        open={openPriorityDialog}
        onClose={() => {
          setOpenPriorityDialog(false);
        }}
        updateStatus={updateStatus}
        updateStatusId={updateStatus.usId}
        updateId={updateStatus.id}
      />

      {openAffectedItemsDialog && (
        <AffectedItemsDialog
          open={openAffectedItemsDialog}
          onClose={() => {
            setOpenAffectedItemsDialog(false);
          }}
          updateId={updateStatus.id}
        />
      )}
    </Container>
  );
};

export { NewTableMenu };
